// =============================================================================
// NotesToHankContext.tsx — React context for Notes-to-Hank text chat (web port)
// Uses localStorage instead of AsyncStorage for persistence.
// =============================================================================

import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  ReactNode,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { API } from 'aws-amplify';
import gql from 'graphql-tag';
import { GraphQLResult } from '@aws-amplify/api-graphql';
import { LOG } from '../../common/Utilities';
import featureFlag from '../../common/featureFlag';
import { getIdToken } from '../../common/utils/authUtils';

import type {
  ChatMessage,
  NoteMessage,
  NoteConnectionState,
  AttachmentItem,
  ConversationContextEntry,
  PhotoMetadata,
  PersistedConversation,
} from '../../services/notesToHank/NotesToHankTypes';
import { NotesToHankAdapter } from '../../services/notesToHank/NotesToHankAdapter';
import {
  buildInitialMessage,
  buildFollowUpMessage,
  buildQuestionPhotoAttachmentsPayload,
  resolvePhotoUri,
} from '../../services/notesToHank/notesToHankMessageUtils';
import { generateMockResponse } from '../../services/notesToHank/mockNotesToHankResponse';
import {
  uploadPhoto,
  buildCloudFrontUrl,
} from '../../services/notesToHank/photoUploadService';
import { GamifyToast } from '../../common/CustomToasts';

const LOG_PREFIX = '[NTH-Context]';

// -----------------------------------------------------------------------------
// GraphQL Query — Load conversation history from DynamoDB
// -----------------------------------------------------------------------------

const LIST_INTAKE_CONVERSATION_BY_TASK_LIST_ID = gql`
  query ListIntakeConversationByTaskListID(
    $taskListID: String!
    $sortDirection: ModelSortDirection
    $limit: Int
    $nextToken: String
  ) {
    listIntakeConversationByTaskListID(
      taskListID: $taskListID
      sortDirection: $sortDirection
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        role
        text
        eventType
        questions
        actionButtons
        selectedAnswerIDs
        selectedAnswersMap
        questionPhotoAttachments
        pressedActionButtonIDs
        sortOrder
        isDeleted
        createdAt
      }
      nextToken
    }
  }
`;

// -----------------------------------------------------------------------------
// Convert DynamoDB IntakeConversation records to ChatMessage[]
// -----------------------------------------------------------------------------

interface IntakeConversationRecord {
  id: string;
  role: string;
  text: string | null;
  eventType: string;
  questions: string | null; // AWSJSON
  actionButtons: string | null; // AWSJSON
  selectedAnswerIDs: string[] | null;
  selectedAnswersMap: string | null; // AWSJSON — explicit question-to-answer mapping
  questionPhotoAttachments: string | null; // AWSJSON — photo attachments mapped to question IDs
  pressedActionButtonIDs: string[] | null;
  sortOrder: number;
  isDeleted: boolean;
  createdAt: string;
}

function convertRecordsToChatMessages(
  records: IntakeConversationRecord[]
): ChatMessage[] {
  // Filter out deleted, system messages, and initial event types (session start messages)
  const filtered = records
    .filter(
      (r) => !r.isDeleted && r.role !== 'system' && r.eventType !== 'initial'
    )
    .sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );

  const messages: ChatMessage[] = [];

  for (let i = 0; i < filtered.length; i++) {
    const record = filtered[i];
    const sender: 'user' | 'ai' = record.role === 'user' ? 'user' : 'ai';
    const timestamp = new Date(record.createdAt).getTime();

    // Parse AWSJSON fields — single JSON.parse() only.
    // AppSync AWSJSON returns a JSON string; one parse yields the structured data.
    let questions: any[] | undefined;
    if (record.questions) {
      try {
        const parsed = JSON.parse(record.questions);
        if (Array.isArray(parsed) && parsed.length > 0) {
          questions = parsed;
        }
      } catch {
        questions = undefined;
      }
    }

    let actionButtons: any[] | undefined;
    if (record.actionButtons) {
      try {
        const parsed = JSON.parse(record.actionButtons);
        if (Array.isArray(parsed) && parsed.length > 0) {
          actionButtons = parsed;
        }
      } catch {
        actionButtons = undefined;
      }
    }

    // Build selectedAnswers map from selectedAnswerIDs on user messages
    let selectedAnswers: Record<string, string> | undefined;
    if (record.selectedAnswerIDs && record.selectedAnswerIDs.length > 0) {
      selectedAnswers = record.selectedAnswerIDs.reduce(
        (acc, id) => ({ ...acc, [id]: id }),
        {} as Record<string, string>
      );
    }

    // For AI messages with questions, look ahead at the next user message
    // to get the selectedAnswers and attach them on the AI message.
    // This reconstructs the "answered" state so QuestionButtons renders correctly.
    let perQuestionAttachments: Record<string, AttachmentItem[]> | undefined;

    if (sender === 'ai' && questions && questions.length > 0) {
      const nextRecord = filtered[i + 1];
      if (nextRecord && nextRecord.role === 'user') {
        // Prefer selectedAnswersMap (deterministic, explicit mapping)
        if (nextRecord.selectedAnswersMap) {
          try {
            const map = JSON.parse(nextRecord.selectedAnswersMap);
            if (map && typeof map === 'object' && !Array.isArray(map)) {
              selectedAnswers = map;
            }
          } catch {
            // JSON parse failed — fall through to legacy fallback below
            selectedAnswers = undefined;
          }
        }

        // Legacy fallback: positional inference from selectedAnswerIDs
        // Used for old records that don't have selectedAnswersMap
        if (!selectedAnswers || Object.keys(selectedAnswers).length === 0) {
          const answerMap: Record<string, string> = {};

          if (
            nextRecord.selectedAnswerIDs &&
            nextRecord.selectedAnswerIDs.length > 0
          ) {
            // Multi-question mode: map positionally
            for (
              let qi = 0;
              qi < questions.length && qi < nextRecord.selectedAnswerIDs.length;
              qi++
            ) {
              const answerId = nextRecord.selectedAnswerIDs[qi];
              if (answerId) {
                answerMap[questions[qi].id] = answerId;
              }
            }
          } else if (questions.length === 1 && nextRecord.text) {
            // Single-question mode: the user's text IS the answer.
            // Try to match it against the answer options by text (case-insensitive).
            const q = questions[0];
            const userText = nextRecord.text.trim().toLowerCase();
            const matchedAnswer = q.answers?.find(
              (a: any) => a.text.toLowerCase() === userText
            );
            if (matchedAnswer) {
              answerMap[q.id] = matchedAnswer.id;
            } else {
              // No exact match — the user typed a free-text response.
              // Mark the question as answered with a synthetic key so buttons disable.
              answerMap[q.id] = '__text_response__';
            }
          }

          if (Object.keys(answerMap).length > 0) {
            selectedAnswers = answerMap;
          }
        }

        // Attach photo thumbnails from the next user record's questionPhotoAttachments
        if (nextRecord.questionPhotoAttachments) {
          try {
            const mapping: Record<string, string[]> = JSON.parse(
              nextRecord.questionPhotoAttachments
            );
            if (
              mapping &&
              typeof mapping === 'object' &&
              !Array.isArray(mapping)
            ) {
              const attachments: Record<string, AttachmentItem[]> = {};
              for (const [qId, uris] of Object.entries(mapping)) {
                if (Array.isArray(uris)) {
                  attachments[qId] = uris
                    .filter((uri) => typeof uri === 'string' && uri.length > 0)
                    .map((uri) => ({
                      uri: resolvePhotoUri(uri),
                      type: 'photo' as const,
                      timestamp: new Date(nextRecord.createdAt).getTime(),
                      uploadStatus: 'complete' as const,
                    }));
                }
              }
              if (Object.keys(attachments).length > 0) {
                perQuestionAttachments = attachments;
              }
            }
          } catch {
            console.warn(
              `${LOG_PREFIX} Failed to parse questionPhotoAttachments for record`,
              nextRecord.id
            );
            // Graceful degradation — render without thumbnails
          }
        }
      }
    }

    // Skip messages that have no text AND no questions AND no actionButtons
    // (these are empty AI responses that shouldn't render)
    const hasContent =
      (record.text && record.text.trim().length > 0) ||
      questions ||
      actionButtons;
    if (!hasContent) {
      continue;
    }

    const msg: ChatMessage = {
      id: record.id,
      sender,
      text: record.text ?? '',
      timestamp,
      questions,
      actionButtons,
      selectedAnswers,
      perQuestionAttachments,
      pressedActionButtonIds:
        record.pressedActionButtonIDs &&
        record.pressedActionButtonIDs.length > 0
          ? record.pressedActionButtonIDs
          : undefined,
      isError: record.eventType === 'error',
    };

    messages.push(msg);
  }

  return messages;
}

// -----------------------------------------------------------------------------
// localStorage Key Helper
// -----------------------------------------------------------------------------

const getStorageKey = (taskListId: string): string =>
  `notesToHank:${taskListId}`;

// -----------------------------------------------------------------------------
// Context Type
// -----------------------------------------------------------------------------

export interface NotesToHankContextType {
  connectionState: NoteConnectionState;
  messages: ChatMessage[];
  isWaitingForResponse: boolean;
  inputText: string;
  setInputText: (text: string) => void;
  pendingPhotos: AttachmentItem[];
  pendingAttachments: AttachmentItem[];
  selectedAnswerIds: string[];
  pressedActionButtonIds: string[];
  isUploading: boolean;
  activeUploadTaskId: string | null;
  sendMessage: () => void;
  selectAnswer: (
    questionIndex: number,
    answerIndex: number,
    answerId: string,
    answerText?: string,
    questionId?: string
  ) => void;
  submitBatchAnswers: (
    answerIds: string[],
    combinedText: string,
    selectedAnswersMap?: Record<string, string>
  ) => void;
  pressActionButton: (
    actionId: string,
    label?: string,
    clientCommand?: string
  ) => void;
  addAttachment: (attachment: AttachmentItem) => void;
  addAttachments: (attachments: AttachmentItem[]) => void;
  removeAttachment: (index: number) => void;
  perQuestionAttachments: Record<string, AttachmentItem[]>;
  removeQuestionAttachment: (questionId: string, index: number) => void;
  setTaskUploading: (taskId: string) => void;
  setTaskUploadComplete: (taskId: string) => void;
  replaceUploadingAttachments: (
    taskId: string,
    completed: AttachmentItem[],
    errored: AttachmentItem[]
  ) => void;
  retryFailedUpload: (questionId: string, attachmentIndex: number) => void;
  storeFilesForRetry: (taskId: string, files: File[]) => void;
  retryLastMessage: () => void;
  clearConversation: () => void;
}

// -----------------------------------------------------------------------------
// Create Context
// -----------------------------------------------------------------------------

const NotesToHankContext = createContext<NotesToHankContextType | undefined>(
  undefined
);

// -----------------------------------------------------------------------------
// Provider Props
// -----------------------------------------------------------------------------

export interface NotesToHankProviderProps {
  children: ReactNode;
  dealId: string | null | undefined;
  taskListId?: string | null;
  websocketUrl?: string;
  onStartHank?: () => void;
  onStartManual?: () => void;
  onOpenAttachmentPicker?: () => void;
  onOrderEstimate?: () => void;
  onSubmitClaim?: () => void;
}

// -----------------------------------------------------------------------------
// WebSocket URLs
// -----------------------------------------------------------------------------

const NOTES_DEV_WEBSOCKET_URL =
  'ws://paiv-agents-nlb-d254e1a26657601f.elb.us-west-2.amazonaws.com:8080/ws';
const NOTES_PROD_WEBSOCKET_URL =
  'ws://paiv-agents-nlb-d254e1a26657601f.elb.us-west-2.amazonaws.com:8081/ws';

const DEFAULT_NOTES_WEBSOCKET_URL = NOTES_DEV_WEBSOCKET_URL;

export { NOTES_DEV_WEBSOCKET_URL, NOTES_PROD_WEBSOCKET_URL };

// -----------------------------------------------------------------------------
// Provider Component
// -----------------------------------------------------------------------------

export const NotesToHankProvider: React.FC<NotesToHankProviderProps> = ({
  children,
  dealId,
  taskListId,
  websocketUrl = DEFAULT_NOTES_WEBSOCKET_URL,
  onStartHank,
  onStartManual,
  onOpenAttachmentPicker,
  onOrderEstimate,
  onSubmitClaim,
}) => {
  // =========================================================================
  // State
  // =========================================================================

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [connectionState, setConnectionState] =
    useState<NoteConnectionState>('disconnected');
  const [inputText, setInputText] = useState('');
  const [pendingPhotos, setPendingPhotos] = useState<AttachmentItem[]>([]);
  const [pendingAttachments, setPendingAttachments] = useState<
    AttachmentItem[]
  >([]);
  const [selectedAnswerIds, setSelectedAnswerIds] = useState<string[]>([]);
  const [pressedActionButtonIds, setPressedActionButtonIds] = useState<
    string[]
  >([]);

  // Track which task_item_id triggered the current upload (set when "Upload now" is pressed)
  const [activeUploadTaskId, setActiveUploadTaskId] = useState<string | null>(
    null
  );

  // Per-question attachments: maps question/task ID → attached files
  const [perQuestionAttachments, setPerQuestionAttachments] = useState<
    Record<string, AttachmentItem[]>
  >({});

  // Track which task IDs currently have uploads in progress
  const [uploadingTasks, setUploadingTasks] = useState<Set<string>>(new Set());

  // =========================================================================
  // Refs
  // =========================================================================

  const adapterRef = useRef<NotesToHankAdapter | null>(null);
  const hasSentInitialRef = useRef(false);
  const shouldIncludeContextRef = useRef(false);
  const lastUserMessageRef = useRef<NoteMessage | null>(null);
  const dealIdRef = useRef<string | null | undefined>(dealId);
  const taskListIdRef = useRef<string | null | undefined>(taskListId);
  const perQuestionAttachmentsRef = useRef<Record<string, AttachmentItem[]>>(
    {}
  );

  // Store File objects for retry — keyed by `${taskId}-${index}`
  const retryFilesRef = useRef<Map<string, File>>(new Map());

  useEffect(() => {
    dealIdRef.current = dealId;
  }, [dealId]);

  useEffect(() => {
    taskListIdRef.current = taskListId;
  }, [taskListId]);

  // Keep perQuestionAttachments ref in sync for use in persistConversation
  useEffect(() => {
    perQuestionAttachmentsRef.current = perQuestionAttachments;
  }, [perQuestionAttachments]);

  // =========================================================================
  // Build conversation context from messages
  // =========================================================================

  const buildConversationContext =
    useCallback((): ConversationContextEntry[] => {
      return messages.map((msg) => ({
        role: msg.sender === 'user' ? ('client' as const) : ('server' as const),
        message: {
          type: 'note_message' as const,
          event_type:
            msg.sender === 'user'
              ? ('follow_up' as const)
              : ('response' as const),
          deal_id: dealIdRef.current ?? '',
          text: msg.text,
          photos: msg.photos,
          questions: msg.questions,
          action_buttons: msg.actionButtons,
          timestamp: msg.timestamp,
        },
        timestamp: msg.timestamp,
      }));
    }, [messages]);

  // =========================================================================
  // localStorage Persistence
  // =========================================================================

  const persistConversation = useCallback((currentMessages: ChatMessage[]) => {
    const currentTaskListId = taskListIdRef.current;
    if (!currentTaskListId) {
      return;
    }

    try {
      const context = currentMessages.map((msg) => ({
        role: msg.sender === 'user' ? ('client' as const) : ('server' as const),
        message: {
          type: 'note_message' as const,
          event_type:
            msg.sender === 'user'
              ? ('follow_up' as const)
              : ('response' as const),
          deal_id: dealIdRef.current ?? '',
          text: msg.text,
          photos: msg.photos,
          questions: msg.questions,
          action_buttons: msg.actionButtons,
          timestamp: msg.timestamp,
        },
        timestamp: msg.timestamp,
      }));

      // Filter perQuestionAttachments: only persist complete uploads with CloudFront URLs (not blob URLs)
      const persistableAttachments: Record<string, AttachmentItem[]> = {};
      for (const [questionId, attachments] of Object.entries(
        perQuestionAttachmentsRef.current
      )) {
        const filtered = attachments.filter(
          (a) => a.uploadStatus === 'complete' && !a.uri.startsWith('blob:')
        );
        if (filtered.length > 0) {
          persistableAttachments[questionId] = filtered;
        }
      }

      const data: PersistedConversation = {
        messages: currentMessages,
        context,
        perQuestionAttachments:
          Object.keys(persistableAttachments).length > 0
            ? persistableAttachments
            : undefined,
        lastUpdated: Date.now(),
      };

      localStorage.setItem(
        getStorageKey(currentTaskListId),
        JSON.stringify(data)
      );
    } catch (error) {
      LOG.error(
        `${LOG_PREFIX} Failed to persist conversation to localStorage`,
        error
      );
    }
  }, []);

  const loadConversation = useCallback(
    (loadTaskListId: string): PersistedConversation | null => {
      try {
        const raw = localStorage.getItem(getStorageKey(loadTaskListId));
        if (raw === null) {
          return null;
        }
        return JSON.parse(raw) as PersistedConversation;
      } catch (error) {
        LOG.error(
          `${LOG_PREFIX} Failed to load conversation from localStorage`,
          error
        );
        return null;
      }
    },
    []
  );

  // Persist conversation when perQuestionAttachments changes (e.g., upload completes)
  useEffect(() => {
    const hasCompletedAttachments = Object.values(perQuestionAttachments).some(
      (attachments) =>
        attachments.some(
          (a) => a.uploadStatus === 'complete' && !a.uri.startsWith('blob:')
        )
    );
    if (hasCompletedAttachments && messages.length > 0) {
      persistConversation(messages);
    }
  }, [perQuestionAttachments, messages, persistConversation]);

  // =========================================================================
  // Convert pending attachments to PhotoMetadata
  // =========================================================================

  const getPhotoPayload = useCallback((): PhotoMetadata[] => {
    const photos: PhotoMetadata[] = [];

    // Include per-question attachments that have completed S3 uploads
    for (const [questionId, attachments] of Object.entries(
      perQuestionAttachments
    )) {
      for (const att of attachments) {
        if (att.s3Key && att.uploadStatus === 'complete') {
          photos.push({
            uri: att.s3Key, // S3 key for backend (Requirement 5.2)
            timestamp: att.timestamp,
            taskItemId: questionId, // Task this photo belongs to
          });
        }
      }
    }

    // Include global pending attachments (non-S3 backed, legacy flow)
    const allPending = [...pendingPhotos, ...pendingAttachments];
    for (const item of allPending) {
      photos.push({
        uri: item.uri,
        timestamp: item.timestamp,
        ...(activeUploadTaskId ? { taskItemId: activeUploadTaskId } : {}),
      });
    }

    return photos;
  }, [
    pendingPhotos,
    pendingAttachments,
    activeUploadTaskId,
    perQuestionAttachments,
  ]);

  // =========================================================================
  // Adapter Callbacks
  // =========================================================================

  const handleAdapterMessage = useCallback(
    (msg: NoteMessage) => {
      LOG.info(
        `${LOG_PREFIX} WS received: event_type=${
          msg.event_type
        }, hasQuestions=${!!msg.questions?.length}, text="${(
          msg.text ?? ''
        ).slice(0, 60)}"`
      );
      if (msg.event_type === 'response') {
        const aiMessage: ChatMessage = {
          id: uuidv4(),
          sender: 'ai',
          text: msg.text ?? '',
          timestamp: msg.timestamp ?? Date.now(),
          questions: msg.questions,
          actionButtons: msg.action_buttons,
        };
        setMessages((prev) => {
          const updated = [...prev, aiMessage];
          persistConversation(updated);
          return updated;
        });
        setIsWaitingForResponse(false);
      } else if (msg.event_type === 'thinking') {
        // Interim "thinking" message — show it but keep waiting for the real response
        const thinkingMessage: ChatMessage = {
          id: uuidv4(),
          sender: 'ai',
          text: msg.text ?? '',
          timestamp: msg.timestamp ?? Date.now(),
        };
        setMessages((prev) => [...prev, thinkingMessage]);
        // Do NOT set isWaitingForResponse to false — the final response is still coming
      } else if (msg.event_type === 'error') {
        const errorMessage: ChatMessage = {
          id: uuidv4(),
          sender: 'ai',
          text: msg.error ?? 'An error occurred. Please try again.',
          timestamp: msg.timestamp ?? Date.now(),
          isError: true,
        };
        setMessages((prev) => [...prev, errorMessage]);
        setIsWaitingForResponse(false);
      }
    },
    [persistConversation]
  );

  const handleAdapterError = useCallback((error: Error) => {
    LOG.error(`${LOG_PREFIX} Adapter error:`, error.message);

    const errorMessage: ChatMessage = {
      id: uuidv4(),
      sender: 'ai',
      text: error.message || 'Connection error. Please try again.',
      timestamp: Date.now(),
      isError: true,
    };
    setMessages((prev) => [...prev, errorMessage]);
    setIsWaitingForResponse(false);
  }, []);

  const handleConnectionChange = useCallback((state: NoteConnectionState) => {
    LOG.info(`${LOG_PREFIX} Connection state changed: ${state}`);
    setConnectionState(state);

    if (state === 'connected') {
      shouldIncludeContextRef.current = true;

      if (
        !hasSentInitialRef.current &&
        dealIdRef.current &&
        adapterRef.current
      ) {
        LOG.info(
          `${LOG_PREFIX} Connected - sending initial message to get welcome response`
        );
        const initialMsg = buildInitialMessage(
          dealIdRef.current,
          '',
          [],
          undefined,
          taskListIdRef.current ?? undefined
        );
        hasSentInitialRef.current = true;
        shouldIncludeContextRef.current = false;
        setIsWaitingForResponse(true);
        try {
          adapterRef.current.sendMessage(initialMsg);
        } catch (err) {
          LOG.error(
            `${LOG_PREFIX} Failed to send initial message:`,
            String(err)
          );
          setIsWaitingForResponse(false);
        }
      }
    }
  }, []);

  // =========================================================================
  // Adapter Lifecycle
  // =========================================================================

  useEffect(() => {
    if (!dealId || !taskListId) {
      LOG.info(
        `${LOG_PREFIX} No deal ID or task list ID, skipping adapter creation`
      );
      return;
    }

    // Mock mode
    if (featureFlag('mockNotesToHank')) {
      LOG.info(`${LOG_PREFIX} Mock mode enabled - skipping real adapter`);
      setConnectionState('connected');
      hasSentInitialRef.current = false;
      shouldIncludeContextRef.current = false;

      const autoInitial = buildInitialMessage(
        dealId,
        '',
        [],
        undefined,
        taskListId
      );
      hasSentInitialRef.current = true;
      setIsWaitingForResponse(true);
      const delay = 300 + Math.random() * 500;
      const timer = setTimeout(() => {
        const mockResponse = generateMockResponse(autoInitial);
        handleAdapterMessage(mockResponse);
      }, delay);

      return () => {
        clearTimeout(timer);
      };
    }

    LOG.info(
      `${LOG_PREFIX} Creating adapter for deal: ${dealId}, taskList: ${taskListId}`
    );

    const adapter = new NotesToHankAdapter({
      url: websocketUrl,
      getAuthToken: async () => {
        const token = await getIdToken();
        return token ?? '';
      },
    });

    adapter.onMessage = handleAdapterMessage;
    adapter.onError = handleAdapterError;
    adapter.onConnectionChange = handleConnectionChange;

    adapterRef.current = adapter;

    adapter.connect().catch((err) => {
      LOG.error(`${LOG_PREFIX} Failed to connect adapter:`, String(err));
    });

    hasSentInitialRef.current = false;
    shouldIncludeContextRef.current = false;

    return () => {
      LOG.info(
        `${LOG_PREFIX} Cleaning up adapter for deal: ${dealId}, taskList: ${taskListId}`
      );
      adapter.onMessage = null;
      adapter.onError = null;
      adapter.onConnectionChange = null;
      adapter.disconnect();
      adapter.cleanup();
      adapterRef.current = null;
    };
  }, [
    dealId,
    taskListId,
    websocketUrl,
    handleAdapterMessage,
    handleAdapterError,
    handleConnectionChange,
  ]);

  // =========================================================================
  // Load Persisted Conversation on Mount / Deal ID Change
  // =========================================================================

  useEffect(() => {
    if (!taskListId) {
      return;
    }

    let cancelled = false;

    const loadFromDynamoDB = async () => {
      try {
        LOG.info(
          `${LOG_PREFIX} Loading conversation history from DynamoDB for taskList: ${taskListId}`
        );

        let allRecords: IntakeConversationRecord[] = [];
        let nextToken: string | null = null;

        // Paginate through all records
        do {
          const result = (await API.graphql({
            query: LIST_INTAKE_CONVERSATION_BY_TASK_LIST_ID as any,
            variables: {
              taskListID: taskListId,
              sortDirection: 'ASC',
              limit: 100,
              ...(nextToken ? { nextToken } : {}),
            },
          })) as GraphQLResult<any>;

          const data = result?.data?.listIntakeConversationByTaskListID;
          if (data?.items) {
            allRecords = [...allRecords, ...data.items];
          }
          nextToken = data?.nextToken ?? null;
        } while (nextToken);

        if (cancelled) return;

        if (allRecords.length > 0) {
          // Debug: log a sample record to verify questions field shape
          const sampleWithQuestions = allRecords.find((r: any) => r.questions);
          if (sampleWithQuestions) {
            console.log(
              `${LOG_PREFIX} Sample record with questions:`,
              JSON.stringify(sampleWithQuestions, null, 2).slice(0, 500)
            );
            console.log(
              `${LOG_PREFIX} questions field type:`,
              typeof (sampleWithQuestions as any).questions
            );
          }

          const chatMessages = convertRecordsToChatMessages(allRecords);
          LOG.info(
            `${LOG_PREFIX} Loaded ${chatMessages.length} messages from DynamoDB for taskList: ${taskListId}`
          );

          // Debug: log how many messages have questions
          const withQuestions = chatMessages.filter(
            (m) => m.questions && m.questions.length > 0
          );
          console.log(
            `${LOG_PREFIX} Messages with questions: ${withQuestions.length} of ${chatMessages.length}`
          );

          setMessages(chatMessages);

          // Mark that we already have context so the adapter doesn't re-greet
          if (!hasSentInitialRef.current) {
            shouldIncludeContextRef.current = true;
          }

          // Also persist to localStorage as cache
          persistConversation(chatMessages);
          return;
        }

        // No DynamoDB records — fall back to localStorage
        LOG.info(
          `${LOG_PREFIX} No DynamoDB records found, checking localStorage for taskList: ${taskListId}`
        );
        const persisted = loadConversation(taskListId);
        if (persisted && persisted.messages && persisted.messages.length > 0) {
          LOG.info(
            `${LOG_PREFIX} Loaded ${persisted.messages.length} persisted messages from localStorage for taskList: ${taskListId}`
          );
          setMessages(persisted.messages);
          if (!hasSentInitialRef.current) {
            shouldIncludeContextRef.current = true;
          }
          if (
            persisted.perQuestionAttachments &&
            Object.keys(persisted.perQuestionAttachments).length > 0
          ) {
            setPerQuestionAttachments(persisted.perQuestionAttachments);
          }
        } else {
          setMessages([]);
        }
      } catch (error) {
        LOG.error(
          `${LOG_PREFIX} Failed to load conversation from DynamoDB, falling back to localStorage`,
          error
        );

        if (cancelled) return;

        // Fallback to localStorage on GraphQL error
        const persisted = loadConversation(taskListId);
        if (persisted && persisted.messages && persisted.messages.length > 0) {
          setMessages(persisted.messages);
          if (!hasSentInitialRef.current) {
            shouldIncludeContextRef.current = true;
          }
          if (
            persisted.perQuestionAttachments &&
            Object.keys(persisted.perQuestionAttachments).length > 0
          ) {
            setPerQuestionAttachments(persisted.perQuestionAttachments);
          }
        } else {
          setMessages([]);
        }
      }
    };

    loadFromDynamoDB();

    return () => {
      cancelled = true;
    };
  }, [taskListId, loadConversation, persistConversation]);

  // =========================================================================
  // Actions
  // =========================================================================

  const doSend = useCallback(
    (
      overrideText?: string,
      overrideActionIds?: string[],
      overrideAnswerIds?: string[],
      overrideSelectedAnswersMap?: Record<string, string>
    ) => {
      const isMock = featureFlag('mockNotesToHank');
      const adapter = adapterRef.current;
      if (!isMock && !adapter) {
        LOG.warn(`${LOG_PREFIX} Cannot send: no adapter`);
        return;
      }
      if (!dealIdRef.current) {
        LOG.warn(`${LOG_PREFIX} Cannot send: no deal ID`);
        return;
      }

      const currentText = (overrideText ?? inputText).trim();
      const currentPhotos = getPhotoPayload();
      const currentAnswerIds = overrideAnswerIds ?? [...selectedAnswerIds];
      const currentActionIds = overrideActionIds ?? [...pressedActionButtonIds];

      if (
        currentText.length === 0 &&
        currentAnswerIds.length === 0 &&
        currentActionIds.length === 0
      ) {
        return;
      }

      let msg: NoteMessage;

      if (!hasSentInitialRef.current || shouldIncludeContextRef.current) {
        LOG.info(
          `${LOG_PREFIX} doSend: sending as INITIAL (hasSentInitial=${hasSentInitialRef.current}, shouldIncludeContext=${shouldIncludeContextRef.current})`
        );
        const context = shouldIncludeContextRef.current
          ? buildConversationContext()
          : undefined;
        msg = buildInitialMessage(
          dealIdRef.current,
          currentText,
          currentPhotos,
          context,
          taskListIdRef.current ?? undefined
        );
        hasSentInitialRef.current = true;
        shouldIncludeContextRef.current = false;
      } else {
        LOG.info(
          `${LOG_PREFIX} doSend: sending as FOLLOW_UP (text="${currentText.slice(
            0,
            50
          )}...", photos=${currentPhotos.length}, answerIds=${
            currentAnswerIds.length
          }, ids=[${currentAnswerIds.slice(0, 3).join(',')}])`
        );
        msg = buildFollowUpMessage(
          dealIdRef.current,
          currentText,
          currentPhotos,
          currentAnswerIds,
          currentActionIds,
          overrideSelectedAnswersMap,
          buildQuestionPhotoAttachmentsPayload(
            perQuestionAttachmentsRef.current
          )
        );
      }

      lastUserMessageRef.current = msg;

      const userMessage: ChatMessage = {
        id: uuidv4(),
        sender: 'user',
        text: currentText,
        timestamp: Date.now(),
        photos: currentPhotos.length > 0 ? currentPhotos : undefined,
        selectedAnswers:
          currentAnswerIds.length > 0
            ? currentAnswerIds.reduce(
                (acc, id) => ({ ...acc, [id]: id }),
                {} as Record<string, string>
              )
            : undefined,
        pressedActionButtonIds:
          currentActionIds.length > 0 ? currentActionIds : undefined,
      };
      setMessages((prev) => {
        const updated = [...prev, userMessage];
        persistConversation(updated);
        return updated;
      });

      setIsWaitingForResponse(true);

      if (featureFlag('mockNotesToHank')) {
        const mockDelay = 500 + Math.random() * 1000;
        setTimeout(() => {
          const mockResponse = generateMockResponse(msg);
          handleAdapterMessage(mockResponse);
        }, mockDelay);
      } else {
        if (adapter) {
          adapter.sendMessage(msg);
        }
      }

      setInputText('');
      setPendingPhotos([]);
      setPendingAttachments([]);
      setSelectedAnswerIds([]);
      setPressedActionButtonIds([]);
      setActiveUploadTaskId(null);

      // NOTE: perQuestionAttachments are intentionally NOT cleared after submit.
      // They persist so thumbnails remain visible in disabled state on the AI message.
    },
    [
      inputText,
      selectedAnswerIds,
      pressedActionButtonIds,
      getPhotoPayload,
      buildConversationContext,
      persistConversation,
      handleAdapterMessage,
    ]
  );

  const sendMessage = useCallback(() => {
    doSend();
  }, [doSend]);

  const selectAnswer = useCallback(
    (
      _questionIndex: number,
      _answerIndex: number,
      answerId: string,
      answerText?: string,
      questionId?: string
    ) => {
      console.log('[NTH-Context] selectAnswer called:', {
        answerId,
        answerText,
        questionId,
      });

      // If the answer is "Upload now", store the task ID and trigger the file picker
      if (answerId === 'upload_now') {
        console.log(
          '[NTH-Context] upload_now detected, questionId:',
          questionId
        );
        if (questionId) {
          setActiveUploadTaskId(questionId);
          console.log('[NTH-Context] Set activeUploadTaskId:', questionId);
        }
        console.log(
          '[NTH-Context] Calling onOpenAttachmentPicker, exists:',
          !!onOpenAttachmentPicker
        );
        onOpenAttachmentPicker?.();
        return;
      }

      setSelectedAnswerIds((prev) => {
        if (prev.includes(answerId)) {
          return prev;
        }
        return [...prev, answerId];
      });
      // Only trigger send if answerText is a non-empty string.
      // When batching multiple questions, earlier answers pass empty string
      // and only the final submit triggers the send.
      if (answerText && answerText.length > 0) {
        // For single-question mode: write selectedAnswers onto the AI message
        // so it persists across page reloads
        if (questionId) {
          setMessages((prev) => {
            const updated = [...prev];
            for (let i = updated.length - 1; i >= 0; i--) {
              if (
                updated[i].sender === 'ai' &&
                updated[i].questions &&
                updated[i].questions!.length > 0 &&
                !updated[i].selectedAnswers
              ) {
                updated[i] = {
                  ...updated[i],
                  selectedAnswers: {
                    ...(updated[i].selectedAnswers || {}),
                    [questionId]: answerId,
                  },
                };
                break;
              }
            }
            return updated;
          });
        }
        doSend(
          answerText,
          undefined,
          undefined,
          questionId ? { [questionId]: answerId } : undefined
        );
      }
    },
    [doSend, onOpenAttachmentPicker]
  );

  const submitBatchAnswers = useCallback(
    (
      answerIds: string[],
      combinedText: string,
      selectedAnswersMap?: Record<string, string>
    ) => {
      // Set all answer IDs at once (for state consistency)
      setSelectedAnswerIds((prev) => {
        const newIds = answerIds.filter((id) => !prev.includes(id));
        return [...prev, ...newIds];
      });

      // Write selectedAnswers and perQuestionAttachments onto the most recent AI message
      // that has questions so that on reload, QuestionButtons renders correctly
      if (selectedAnswersMap && Object.keys(selectedAnswersMap).length > 0) {
        setMessages((prev) => {
          // Find the last AI message with questions (the one being answered)
          const updated = [...prev];
          for (let i = updated.length - 1; i >= 0; i--) {
            if (
              updated[i].sender === 'ai' &&
              updated[i].questions &&
              updated[i].questions!.length > 0 &&
              !updated[i].selectedAnswers
            ) {
              // Build perQuestionAttachments for questions in this batch
              const questionIds = Object.keys(selectedAnswersMap);
              const relevantAttachments: Record<string, AttachmentItem[]> = {};
              for (const qId of questionIds) {
                if (
                  perQuestionAttachmentsRef.current[qId] &&
                  perQuestionAttachmentsRef.current[qId].length > 0
                ) {
                  relevantAttachments[qId] =
                    perQuestionAttachmentsRef.current[qId];
                }
              }

              updated[i] = {
                ...updated[i],
                selectedAnswers: selectedAnswersMap,
                ...(Object.keys(relevantAttachments).length > 0 && {
                  perQuestionAttachments: relevantAttachments,
                }),
              };
              break;
            }
          }
          return updated;
        });
      }

      // Pass answerIds directly to doSend to avoid stale closure issue
      // (setSelectedAnswerIds is async, so doSend wouldn't see the new IDs yet)
      doSend(combinedText, undefined, answerIds, selectedAnswersMap);
    },
    [doSend]
  );

  const pressActionButton = useCallback(
    (actionId: string, label?: string, clientCommand?: string) => {
      setPressedActionButtonIds((prev) => {
        if (prev.includes(actionId)) {
          return prev;
        }
        return [...prev, actionId];
      });

      if (clientCommand) {
        const localRequestMessage: ChatMessage = {
          id: uuidv4(),
          sender: 'user',
          text: label
            ? `${label} (local request)`
            : `Action: ${clientCommand} (local request)`,
          timestamp: Date.now(),
          pressedActionButtonIds: [actionId],
        };
        setMessages((prev) => {
          const updated = [...prev, localRequestMessage];
          persistConversation(updated);
          return updated;
        });

        switch (clientCommand) {
          case 'hank':
            LOG.info(
              `${LOG_PREFIX} Executing clientCommand: hank (start AI Note)`
            );
            onStartHank?.();
            break;
          case 'manual':
            LOG.info(
              `${LOG_PREFIX} Executing clientCommand: manual (start Manual AI Note)`
            );
            onStartManual?.();
            break;
          case 'upload':
            LOG.info(
              `${LOG_PREFIX} Executing clientCommand: upload (open attachment picker)`
            );
            onOpenAttachmentPicker?.();
            break;
          case 'orderEstimate':
            LOG.info(`${LOG_PREFIX} Executing clientCommand: orderEstimate`);
            onOrderEstimate?.();
            break;
          case 'submitClaim':
            LOG.info(`${LOG_PREFIX} Executing clientCommand: submitClaim`);
            onSubmitClaim?.();
            break;
          default:
            LOG.warn(`${LOG_PREFIX} Unknown clientCommand: ${clientCommand}`);
        }
        return;
      }

      if (label) {
        doSend(label, [actionId]);
      }
    },
    [
      doSend,
      onStartHank,
      onStartManual,
      onOpenAttachmentPicker,
      onOrderEstimate,
      onSubmitClaim,
      persistConversation,
    ]
  );

  const addAttachment = useCallback(
    (attachment: AttachmentItem) => {
      // If there's an active upload task, route to per-question attachments
      if (activeUploadTaskId) {
        const attachmentWithStatus: AttachmentItem = {
          ...attachment,
          uploadStatus: attachment.uploadStatus || 'uploading',
        };
        setPerQuestionAttachments((prev) => ({
          ...prev,
          [activeUploadTaskId]: [
            ...(prev[activeUploadTaskId] || []),
            attachmentWithStatus,
          ],
        }));
        return;
      }
      // Otherwise, add to global pending attachments
      if (attachment.type === 'photo') {
        setPendingPhotos((prev) => [...prev, attachment]);
      } else {
        setPendingAttachments((prev) => [...prev, attachment]);
      }
    },
    [activeUploadTaskId]
  );

  const addAttachments = useCallback(
    (attachments: AttachmentItem[]) => {
      // If there's an active upload task, route all to per-question attachments
      if (activeUploadTaskId) {
        const attachmentsWithStatus = attachments.map((a) => ({
          ...a,
          uploadStatus: a.uploadStatus || ('uploading' as const),
        }));
        setPerQuestionAttachments((prev) => ({
          ...prev,
          [activeUploadTaskId]: [
            ...(prev[activeUploadTaskId] || []),
            ...attachmentsWithStatus,
          ],
        }));
        return;
      }
      const photos = attachments.filter((a) => a.type === 'photo');
      const files = attachments.filter((a) => a.type === 'file');
      if (photos.length > 0) {
        setPendingPhotos((prev) => [...prev, ...photos]);
      }
      if (files.length > 0) {
        setPendingAttachments((prev) => [...prev, ...files]);
      }
    },
    [activeUploadTaskId]
  );

  const removeQuestionAttachment = useCallback(
    (questionId: string, index: number) => {
      setPerQuestionAttachments((prev) => {
        const current = prev[questionId] || [];
        const updated = current.filter((_, i) => i !== index);
        if (updated.length === 0) {
          const { [questionId]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [questionId]: updated };
      });
    },
    []
  );

  // =========================================================================
  // Upload Tracking Helpers
  // =========================================================================

  const isUploading = useMemo(() => uploadingTasks.size > 0, [uploadingTasks]);

  const setTaskUploading = useCallback((taskId: string) => {
    setUploadingTasks((prev) => {
      const next = new Set(prev);
      next.add(taskId);
      return next;
    });
  }, []);

  const setTaskUploadComplete = useCallback((taskId: string) => {
    setUploadingTasks((prev) => {
      const next = new Set(prev);
      next.delete(taskId);
      return next;
    });
  }, []);

  /**
   * Replace uploading placeholder attachments for a task with completed/errored results.
   * Removes all attachments with uploadStatus === 'uploading' for the given taskId,
   * then appends the completed and errored attachments.
   * Revokes blob URLs from placeholders to prevent memory leaks.
   */
  const replaceUploadingAttachments = useCallback(
    (
      taskId: string,
      completed: AttachmentItem[],
      errored: AttachmentItem[]
    ) => {
      setPerQuestionAttachments((prev) => {
        const current = prev[taskId] || [];
        // Revoke blob URLs from uploading placeholders before removing them
        current.forEach((a) => {
          if (a.uploadStatus === 'uploading' && a.uri.startsWith('blob:')) {
            try {
              URL.revokeObjectURL(a.uri);
            } catch {
              // Ignore revoke errors
            }
          }
        });
        // Keep non-uploading attachments (previously completed ones)
        const withoutPlaceholders = current.filter(
          (a) => a.uploadStatus !== 'uploading'
        );
        return {
          ...prev,
          [taskId]: [...withoutPlaceholders, ...completed, ...errored],
        };
      });
    },
    []
  );

  const removeAttachment = useCallback(
    (index: number) => {
      const photoCount = pendingPhotos.length;
      if (index < photoCount) {
        setPendingPhotos((prev) => prev.filter((_, i) => i !== index));
      } else {
        const adjustedIndex = index - photoCount;
        setPendingAttachments((prev) =>
          prev.filter((_, i) => i !== adjustedIndex)
        );
      }
    },
    [pendingPhotos.length]
  );

  /**
   * Store File objects for potential retry. Called during initial upload.
   * Files are keyed by `${taskId}-${index}` where index is the position
   * in the per-question attachments array at the time of upload.
   */
  const storeFilesForRetry = useCallback((taskId: string, files: File[]) => {
    files.forEach((file) => {
      // Store with a key that includes the file name for lookup during retry
      retryFilesRef.current.set(`${taskId}-${file.name}`, file);
    });
  }, []);

  /**
   * Retry a failed upload. Looks up the stored File by taskId + fileName,
   * sets the attachment back to 'uploading', re-attempts uploadPhoto,
   * and updates the attachment on success or shows a toast on failure.
   */
  const retryFailedUpload = useCallback(
    async (questionId: string, attachmentIndex: number) => {
      const attachments = perQuestionAttachments[questionId] || [];
      const failedAttachment = attachments[attachmentIndex];
      if (!failedAttachment || failedAttachment.uploadStatus !== 'error') {
        return;
      }

      // Look up the stored File for retry
      const fileName = failedAttachment.fileName || '';
      const fileKey = `${questionId}-${fileName}`;
      const file = retryFilesRef.current.get(fileKey);

      if (!file) {
        // File not available (e.g., page was refreshed) — remove the errored
        // attachment and let the user re-pick via the file picker
        GamifyToast.error(
          'Retry Unavailable',
          'Please remove and re-attach the photo'
        );
        return;
      }

      const orgId = (global as any)?.me?.orgID;
      const userId = (global as any)?.me?.id;

      if (!orgId || !userId || !dealIdRef.current) {
        GamifyToast.error('Retry Failed', 'Missing user or deal information');
        return;
      }

      // Set the attachment back to 'uploading'
      setPerQuestionAttachments((prev) => {
        const current = prev[questionId] || [];
        const updated = [...current];
        updated[attachmentIndex] = {
          ...updated[attachmentIndex],
          uploadStatus: 'uploading',
          errorMessage: undefined,
        };
        return { ...prev, [questionId]: updated };
      });

      // Mark task as uploading
      setUploadingTasks((prev) => {
        const next = new Set(prev);
        next.add(questionId);
        return next;
      });

      try {
        // Determine sequence number (1-based position among all attachments for this task)
        const sequence = attachmentIndex + 1;
        const result = await uploadPhoto(
          file,
          dealIdRef.current,
          questionId,
          orgId,
          userId,
          sequence
        );

        // Success — replace the attachment with the completed version
        setPerQuestionAttachments((prev) => {
          const current = prev[questionId] || [];
          const updated = [...current];
          updated[attachmentIndex] = {
            uri: buildCloudFrontUrl(result.s3Key),
            type: 'photo' as const,
            s3Key: result.s3Key,
            timestamp: Date.now(),
            uploadStatus: 'complete' as const,
          };
          return { ...prev, [questionId]: updated };
        });

        // Clean up the stored file reference
        retryFilesRef.current.delete(fileKey);
      } catch (err: any) {
        // Failure — show toast and keep error state
        GamifyToast.error(
          'Upload Failed',
          `Failed to upload ${fileName}. Tap retry to try again.`
        );
        setPerQuestionAttachments((prev) => {
          const current = prev[questionId] || [];
          const updated = [...current];
          updated[attachmentIndex] = {
            ...updated[attachmentIndex],
            uploadStatus: 'error' as const,
            errorMessage: err?.message || 'Upload failed',
          };
          return { ...prev, [questionId]: updated };
        });
      } finally {
        // Mark task upload complete
        setUploadingTasks((prev) => {
          const next = new Set(prev);
          next.delete(questionId);
          return next;
        });
      }
    },
    [perQuestionAttachments]
  );

  const retryLastMessage = useCallback(() => {
    const adapter = adapterRef.current;
    const lastMsg = lastUserMessageRef.current;
    if (!adapter || !lastMsg) {
      LOG.warn(`${LOG_PREFIX} Cannot retry: no adapter or last message`);
      return;
    }

    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last?.isError) {
        return prev.slice(0, -1);
      }
      return prev;
    });

    adapter.sendMessage(lastMsg);
    setIsWaitingForResponse(true);
  }, []);

  const clearConversation = useCallback(() => {
    setMessages([]);
    setIsWaitingForResponse(false);
    setInputText('');
    setPendingPhotos([]);
    setPendingAttachments([]);
    setSelectedAnswerIds([]);
    setPressedActionButtonIds([]);
    setPerQuestionAttachments({});
    hasSentInitialRef.current = false;
    shouldIncludeContextRef.current = false;
    lastUserMessageRef.current = null;

    const currentTaskListId = taskListIdRef.current;
    if (currentTaskListId) {
      try {
        localStorage.removeItem(getStorageKey(currentTaskListId));
      } catch (error) {
        LOG.error(
          `${LOG_PREFIX} Failed to clear persisted conversation`,
          error
        );
      }
    }
  }, []);

  // =========================================================================
  // Context Value
  // =========================================================================

  const contextValue = useMemo<NotesToHankContextType>(
    () => ({
      connectionState,
      messages,
      isWaitingForResponse,
      inputText,
      setInputText,
      pendingPhotos,
      pendingAttachments,
      selectedAnswerIds,
      pressedActionButtonIds,
      isUploading,
      activeUploadTaskId,
      sendMessage,
      selectAnswer,
      submitBatchAnswers,
      pressActionButton,
      addAttachment,
      addAttachments,
      removeAttachment,
      perQuestionAttachments,
      removeQuestionAttachment,
      setTaskUploading,
      setTaskUploadComplete,
      replaceUploadingAttachments,
      retryFailedUpload,
      storeFilesForRetry,
      retryLastMessage,
      clearConversation,
    }),
    [
      connectionState,
      messages,
      isWaitingForResponse,
      inputText,
      pendingPhotos,
      pendingAttachments,
      selectedAnswerIds,
      pressedActionButtonIds,
      isUploading,
      activeUploadTaskId,
      sendMessage,
      selectAnswer,
      submitBatchAnswers,
      pressActionButton,
      addAttachment,
      addAttachments,
      removeAttachment,
      perQuestionAttachments,
      removeQuestionAttachment,
      setTaskUploading,
      setTaskUploadComplete,
      replaceUploadingAttachments,
      retryFailedUpload,
      storeFilesForRetry,
      retryLastMessage,
      clearConversation,
    ]
  );

  return (
    <NotesToHankContext.Provider value={contextValue}>
      {children}
    </NotesToHankContext.Provider>
  );
};

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

export const useNotesToHank = (): NotesToHankContextType => {
  const context = useContext(NotesToHankContext);
  if (!context) {
    throw new Error('useNotesToHank must be used within a NotesToHankProvider');
  }
  return context;
};
